import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./ThresholdBanner.module.css";
import type { ThresholdBannerResponse } from "../../api/reporting";

/** The least time the banner waits after the page has drawn, so it never arrives together with the charts. */
const SettleMs = 600;

/**
 * Calls back once the page has settled: after the browser has painted and, where it says so, has nothing else to do,
 * and no sooner than {@link SettleMs}. The charts draw in that time, so the banner is the only thing then moving.
 *
 * @param callback - Called once the page has settled.
 * @returns Cancels the wait.
 */
function whenSettled(callback: () => void): () => void {
  let idle: number | undefined;
  const timer = window.setTimeout(() => {
    if (typeof window.requestIdleCallback === "function") {
      idle = window.requestIdleCallback(callback, { timeout: 1000 });
    } else {
      callback();
    }
  }, SettleMs);
  return () => {
    window.clearTimeout(timer);
    if (idle !== undefined) {
      window.cancelIdleCallback(idle);
    }
  };
}

/**
 * The threshold banner (`PD-24`, `UR-E-10`): computed on read from the rollup at 50/75/90/100%,
 * dismissible, with a per-person, per-period, per-threshold acknowledgement so it stops nagging
 * once seen. Renders nothing when there is nothing pending, rather than an empty panel.
 *
 * It waits until the figures and charts have drawn, then slowly opens downwards, so the page below slides down rather
 * than jumping, and only one thing moves at a time on a phone or a slow computer. It closes the same way when
 * dismissed. Give it a `key` per threshold, so a new crossing opens afresh.
 *
 * @param props.banner - The single most urgent unacknowledged threshold crossing, or `null`.
 * @param props.vendorDisplayName - The vendor's display name, for the copy to name it precisely.
 * @param props.onDismiss - Called when the person dismisses the banner.
 * @param props.dismissing - Whether the dismissal request is in flight, to disable the button.
 */
export function ThresholdBanner({
  banner,
  vendorDisplayName,
  onDismiss,
  dismissing,
}: {
  banner: ThresholdBannerResponse | null;
  vendorDisplayName: string;
  onDismiss: () => void;
  dismissing: boolean;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<"waiting" | "shown" | "leaving" | "gone">("waiting");

  useEffect(() => (phase === "waiting" && banner !== null ? whenSettled(() => setPhase("shown")) : undefined), [phase, banner]);

  if (banner === null || phase === "waiting" || phase === "gone") {
    return null;
  }

  const dismiss = (): void => {
    // With reduced motion there is no transition to wait for, so the banner goes at once.
    setPhase(window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ? "gone" : "leaving");
    onDismiss();
  };

  return (
    <div
      className={phase === "leaving" ? `${styles.wrap} ${styles.leaving}` : styles.wrap}
      onTransitionEnd={(event) => {
        if (phase === "leaving" && event.target === event.currentTarget) {
          setPhase("gone");
        }
      }}
    >
      <div className={styles.banner} role="alert">
        <span className={styles.icon} aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" focusable="false">
            <path d="M3.5 16a8.5 8.5 0 1 1 17 0" />
            <path d="M12 16l4-5" />
            <circle cx="12" cy="16" r="1" />
          </svg>
        </span>
        <p className={styles.message}>
          {t("consumption.threshold.message", {
            percent: Math.round(banner.percentUsed),
            threshold: banner.thresholdPercent,
            vendor: vendorDisplayName,
          })}
        </p>
        <button type="button" className={styles.dismiss} onClick={dismiss} disabled={dismissing || phase === "leaving"}>
          {t("consumption.threshold.dismiss")}
        </button>
      </div>
    </div>
  );
}
